import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import { Card } from 'reactstrap';

import CallexLoader from '../../../components/CallexLoader';
import { checkModulePermission } from '../../../utils/common';
import AdminHeader from '../../../components/header/AdminHeader';
import Pagination from '../../../components/form/Pagination';
import AddEditAIDispositionModal from './AddEditAIDispositionModal';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';

import {
  aiDispositionDelete,
  aiDispositionListGet,
  useAiDispositionList
} from '../../../store/slices/tenantSlices/settingSlice';
import { useUserRolePermission } from '../../../store/slices/authSlice';

const AIDisposition = () => {
  const dispatch = useDispatch();
  const aiDispositionList = useAiDispositionList();
  const userRolePermission = useUserRolePermission();

  const [isLoading, setIsLoading] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isEdit, setIsEdit] = useState(null);
  const [isDeleteId, setIsDeleteId] = useState(null);

  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined
  });

  const handleFormSubmit = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter };
      await dispatch(aiDispositionListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  useEffect(() => {
    handleFormSubmit();
  }, [filter]);

  const deleteDispo = async () => {
    try {
      const response = await dispatch(aiDispositionDelete(isDeleteId)).unwrap();
      if (response && response.success) {
        toast.success(response.message || 'Record deleted successfully');
        handleFormSubmit();
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  return (
    <div>
      <AdminHeader
        title="AI Disposition"
        isExpandSearch
        searchPlaceholder="Search AI Disposition"
        search={filter.search}
        handelSearch={(e) => setFilter({ ...filter, page: 1, search: e.target.value })}
        isAddDisabled={!checkModulePermission('is_add', 'AI Disposition', userRolePermission)}
        isAdd
        addBtnName="Add AI Disposition"
        onAddClick={() => setOpenAddModal(true)}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : aiDispositionList?.data?.length > 0 &&
        checkModulePermission('is_view', 'AI Disposition', userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>Bot</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th className="text-center" style={{ width: '90px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {aiDispositionList?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      <td>{val?.bot_name || val?.bot_id || '-'}</td>
                      <td>{val?.name || '-'}</td>
                      <td>{val?.description || '-'}</td>
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div
                            className={`cursor-pointer text-info ${
                              !checkModulePermission(
                                'is_edit',
                                'AI Disposition',
                                userRolePermission
                              )
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Edit
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission(
                                    'is_edit',
                                    'AI Disposition',
                                    userRolePermission
                                  )
                                ) {
                                  setEditRecord(val);
                                  setIsEdit(true);
                                  setOpenAddModal(true);
                                }
                              }}
                            />
                          </div>
                          <div
                            className={`cursor-pointer text-danger ${
                              !checkModulePermission(
                                'is_delete',
                                'AI Disposition',
                                userRolePermission
                              )
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Trash
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission(
                                    'is_delete',
                                    'AI Disposition',
                                    userRolePermission
                                  )
                                ) {
                                  setIsDeleteId(val?.id);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={aiDispositionList?.total}
              limit={aiDispositionList?.limit}
              page={aiDispositionList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => handleFormSubmit()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteDispo()}
      />
      <AddEditAIDispositionModal
        isOpen={openAddModal}
        onCloseClick={(apiCall = false) => {
          setOpenAddModal(false);
          setEditRecord(null);
          setIsEdit(false);
          if (apiCall) {
            handleFormSubmit();
          }
        }}
        editRecord={editRecord}
        isEdit={isEdit}
      />
    </div>
  );
};

export default AIDisposition;
